/**
 * Word Collection AI Feedback Hook
 *
 * Provides access to word collection AI feedback state and actions.
 * Consumes WordCollectionAIContext and exposes feedback/loading/dismiss.
 */

import { useWordCollectionAIContext } from '../contexts/WordCollectionAIContext'
import type { AIFeedbackItem } from '../components/debug'

/**
 * Hook return type
 */
export interface UseWordCollectionAIFeedback {
  /** Current feedback item (null if none) */
  feedback: AIFeedbackItem | null
  /** Is feedback being generated? */
  isLoading: boolean
  /** Dismiss current feedback (persists for 1 hour) */
  dismiss: () => void
}

/**
 * Access word collection AI feedback state.
 *
 * Must be used within WordCollectionAIProvider.
 *
 * @returns Feedback state and dismiss action
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { feedback, isLoading, dismiss } = useWordCollectionAIFeedback()
 *
 *   if (!feedback) return null
 *
 *   return (
 *     <AIFeedbackOverlay
 *       feedback={[feedback]}
 *       isVisible={true}
 *       onClose={dismiss}
 *     />
 *   )
 * }
 * ```
 */
export function useWordCollectionAIFeedback(): UseWordCollectionAIFeedback {
  const { feedback, isLoading, dismiss } = useWordCollectionAIContext()

  return {
    feedback,
    isLoading,
    dismiss
  }
}
